"use client";

import { forwardRef } from "react";
import MuxVideoElement from "@mux/mux-video-react";
import {
  VIDEO_ASSETS,
  getMuxThumbnail,
  type VideoAssetKey,
} from "@/lib/video-assets";

export interface MuxVideoProps
  extends Omit<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    "src" | "poster" | "autoPlay" | "preload"
  > {
  preload?: "" | "none" | "metadata" | "auto";
  /** Type-safe key into VIDEO_ASSETS */
  assetKey?: VideoAssetKey;
  /** Raw Mux playback ID (use when assetKey isn't available) */
  playbackId?: string;
  /** Override autoPlay (defaults to true for decorative usage) */
  autoPlay?: boolean;
  /** Poster image URL override */
  poster?: string;
}

const MuxVideo = forwardRef<HTMLVideoElement, MuxVideoProps>(
  (
    { assetKey, playbackId: rawPlaybackId, autoPlay = true, poster, ...props },
    ref
  ) => {
    const resolvedPlaybackId = assetKey
      ? VIDEO_ASSETS[assetKey].playbackId
      : rawPlaybackId;

    if (!resolvedPlaybackId) return null;

    const resolvedPoster =
      poster ??
      (assetKey ? getMuxThumbnail(assetKey, { width: 1280 }) : undefined);

    return (
      <MuxVideoElement
        ref={ref as React.Ref<HTMLVideoElement | undefined>}
        playbackId={resolvedPlaybackId}
        autoPlay={autoPlay}
        muted
        loop
        playsInline
        poster={resolvedPoster}
        streamType="on-demand"
        {...props}
      />
    );
  }
);

MuxVideo.displayName = "MuxVideo";

export { MuxVideo };
